import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { HrailTool, InsertHrailToolBody, UpdateHrailToolBody } from "./types/tools";

export const useHrailToolsApi = () => {
  const api = useApi();

  const getAdminHrailTool = useCallback(
    (toolId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/tools/${toolId}`;
      return useSWRMutation<HrailTool, CustomError, string>(url, (url) =>
        api.get<HrailTool>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailTools = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/tools`, query);
      return useSWRImmutable<Paginated<HrailTool>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailTool>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailTools = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/tools`, query);
      return api.get<Paginated<HrailTool>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailTool = useCallback(
    async (body: InsertHrailToolBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailToolBody>(`/admin/settings/hrail/tools`, body);
      mutateCache([`/admin/settings/hrail/tools`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailTool = useCallback(
    async (toolId: string, body: UpdateHrailToolBody): Promise<void> => {
      const url = `/admin/settings/hrail/tools/${toolId}`;
      await api.put<UpdateHrailToolBody>(url, body);
      mutateCache([`/admin/settings/hrail/tools`], url);
    },
    [api],
  );

  const updateAdminHrailToolVisibility = useCallback(
    async (toolId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/tools/${toolId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/tools`]);
    },
    [api],
  );

  const updateAdminHrailToolOrder = useCallback(
    async (toolId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/tools/${toolId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/tools`]);
    },
    [api],
  );

  const deleteAdminHrailTool = useCallback(
    async (toolId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/tools/${toolId}`);
      mutateCache([`/admin/settings/hrail/tools`]);
    },
    [api],
  );

  return {
    getAdminHrailTool,
    useAdminHrailTools,
    getAdminHrailTools,
    insertAdminHrailTool,
    updateAdminHrailTool,
    updateAdminHrailToolVisibility,
    updateAdminHrailToolOrder,
    deleteAdminHrailTool,
  };
};
